<template>
  <div class="status-con">
    <base-tracker-info-modal
      v-if="!disabled && isModalOpen"
      :tracker-type="trackerType"
      :trackers-info="trackersInfo"
      :issues-types="issueTypes"
      :trackers-issues="trackersIssues"
      :scope="scope"
      :is-group="isGroup"
      @closeModal="closeModal"
    />
    <b-spinner
      v-if="isPending(trackersInfo)"
      variant="primary"
      label="Spinning"
    />
    <div
      v-else
      :class="[{ border: border }, 'icon',
               [isTypeOf('thirdPartyTags') ?
                 [{'count': !thirdPartyTagsHasErrors(trackersInfo)},
                  { 'no-pointer': !isThirdPartyTagClickable }] : '']]"
      v-on="$listeners"
      @click="openModal(isTypeOf('thirdPartyTags') && !isThirdPartyTagClickable)"
    >
      <template
        v-if="isTypeOf('thirdPartyTags') && !thirdPartyTagsHasErrors(trackersInfo)"
      >
        {{ trackersInfo.length }}
      </template>
      <template v-else>
        <i :class="`${statusType(btnStatusType)}`" />
      </template>
    </div>
  </div>
</template>
<script>
import urlHelper from '@/core/mixins/urlHelper';

export default {
  name: 'StatusBtn',
  mixins: [urlHelper],
  props: {
    trackerType: {
      type: String,
      required: true,
      validator(value) {
        // The value must match one of these strings
        return ['impressionTracker', 'clickTracker', 'postClickTracker', 'thirdPartyTags'].includes(value);
      },
    },
    trackersInfo: {
      type: [String, Array, Object],
      required: true,
      default() {
        return {};
      },
    },
    scope: {
      type: String,
      required: true,
      default: 'CONTENT_ITEM',
    },
    isGroup: {
      type: Boolean,
      required: false,
      default: false,
    },
    displayCount: {
      type: Boolean,
      required: false,
      default: false,
    },
    border: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isModalOpen: false,
      btnStatusType: 'spinner',
      issueTypes: new Set(),
      trackersIssues: [],
    };
  },
  computed: {
    isThirdPartyTagClickable() {
      return this.thirdPartyTagsHasErrors(this.trackersInfo)
        || (!this.thirdPartyTagsHasErrors(this.trackersInfo) && !this.disabled);
    },
  },
  methods: {
    openModal(disabled = false) {
      if (disabled) return;
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
    },
    isTypeOf(val) {
      return this.trackerType === val;
    },
    thirdPartyTagsHasErrors(tags) {
      if (!tags || !Array.isArray(tags)) return false;
      return tags.some((tag) => !tag.isValid);
    },
    statusType(type) {
      switch (type) {
        case 'success':
          return 'fas fa-check-circle success';
        case 'warning':
          return 'fas fa-exclamation-triangle warning';
        case 'error':
          return 'fas fa-times-circle error';
        default:
          return 'spinner';
      }
    },
    isPending(trackersInfo) {
      return trackersInfo.some((tracker) => tracker.status === 'PENDING') || this.btnStatusType === 'spinner';
    },
    setBtnStatusType() {
      if (this.issueTypes.has('error')) this.btnStatusType = 'error';
      else if (this.issueTypes.has('warning')) this.btnStatusType = 'warning';
      else this.btnStatusType = 'success';
    },
    getErrorMessage(tracker) {
      return tracker.error ?? 'Unknown Error';
    },
    appendIssue(tracker, issueMessage) {
      issueMessage.includes('ERROR') ? this.issueTypes.add('error') : this.issueTypes.add('warning');
      const issueIndex = this.trackersIssues.findIndex((issue) => issue.type === issueMessage);
      if (issueIndex === -1) {
        this.trackersIssues.push({
          type: issueMessage,
          trackers: [tracker],
        });
      } else {
        this.trackersIssues[issueIndex].trackers.push(tracker);
      }
    },
    checkIsValid(tracker) {
      if (!tracker.isValid) {
        const issueMessage = `ERROR: ${this.getErrorMessage(tracker)}`;
        this.appendIssue(tracker, issueMessage);
      }
    },
    checkIsClickTagEmpty(tracker, trackerType) {
      if (trackerType !== 'clickTracker') return;
      if (!tracker.landingPage) {
        const issueMessage = 'ERROR: EMPTY CLICK TAG!';
        this.appendIssue(tracker, issueMessage);
      }
    },
    checkErrors(tracker, trackerType) {
      this.checkIsValid(tracker);
      this.checkIsClickTagEmpty(tracker, trackerType);
    },
    checkIsClickTagNotMatchingUrl(tracker, trackerType) {
      const trailUrl = (url) => ((url) ? url.trim().replace(/\/$/, '') : url);
      const isMatchingUrls = () => trailUrl(tracker.contentItem.url) === trailUrl(tracker.landingPage);
      if (!tracker.contentItem || trackerType !== 'clickTracker') return;
      if (tracker.landingPage && !isMatchingUrls(tracker)) {
        const issueMessage = 'WARNING: URL Doesn\'t Match';
        this.appendIssue(tracker, issueMessage);
      }
    },
    checkWarnings(tracker, trackerType) {
      this.checkIsClickTagNotMatchingUrl(tracker, trackerType);
    },
    checkTrackersIssues({ trackerType, trackersInfo }) {
      const isEmptyTrackersInfo = () => {
        if (Array.isArray(trackersInfo) && !trackersInfo.length) return true;
        if (trackersInfo && trackersInfo.constructor === Object && !Object.values(trackersInfo).length) return true;
        return !trackersInfo;
      };
      if (!trackerType || isEmptyTrackersInfo()) return [];
      for (let index = 0; index < trackersInfo.length; index++) {
        const tracker = trackersInfo[index];
        if (!tracker) continue;
        this.checkErrors(tracker, trackerType);
        this.checkWarnings(tracker, trackerType);
      }
    },
  },
  async mounted() {
    const { trackerType, trackersInfo } = this;
    this.checkTrackersIssues({ trackerType, trackersInfo });
    this.setBtnStatusType();
  },
};
</script>
<style lang="scss" scoped>
.status-con {
  display: flex;
  justify-content: center;
  .icon {
    background: transparent;
    border: none;
    font-size: 25px;
    width: 45px;
    height: 45px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }
}
</style>
